import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, CheckCircle2, X, AlertTriangle, Clock, Zap, ArrowRight } from 'lucide-react';

const StoreSetupProgress = ({ catalog }) => {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    // Payment setup status
    const hasVerifiedBankAccount = catalog?.paymentSettings?.bankAccount?.verificationStatus === 'VERIFIED';
    const hasOnlinePaymentsEnabled = catalog?.allowOnlinePayment || false;

    // Check if catalog has enough products, shipping options, and logo
    const hasEnoughProducts = (catalog?.productsCount || 0) >= 3;
    const hasShippingOptions = Array.isArray(catalog?.shippingOptions) && catalog.shippingOptions.length > 0;
    const hasLogo = !!catalog?.logo;

    // Calculate social links
    const socialLinks = [];
    if (catalog?.socialMedia) {
        Object.entries(catalog.socialMedia).forEach(([key, value]) => {
            if (key !== '__typename' && value && value.trim() !== '') {
                socialLinks.push(key);
            }
        });
    }

    // Setup tasks with enhanced metadata
    const setupTasks = [
        {
            id: 'payment',
            title: 'Payment Setup',
            description: 'Enable online payments to start earning',
            completed: hasVerifiedBankAccount && hasOnlinePaymentsEnabled,
            action: () => navigate('/payment-settings'),
            priority: 'critical',
            estimatedTime: '1 min',
            icon: '💳',
            impact: 'Start receiving payments immediately'
        },
        {
            id: 'products',
            title: 'Add Products',
            description: `Add at least 3 products (${catalog?.productsCount || 0}/3)`,
            completed: hasEnoughProducts,
            action: () => navigate('/catalog'),
            priority: 'critical',
            estimatedTime: '3 min',
            icon: '📦',
            impact: 'Make your store ready for customers'
        },
        {
            id: 'shipping',
            title: 'Shipping Options',
            description: 'Set up delivery methods for customers',
            completed: hasShippingOptions,
            action: () => navigate('/shipping-settings'),
            priority: 'important',
            estimatedTime: '2 min',
            icon: '🚚',
            impact: 'Enable order fulfillment'
        },
        {
            id: 'logo',
            title: 'Store Branding',
            description: 'Upload your logo for professional appearance',
            completed: hasLogo,
            action: () => navigate('/personalization'),
            priority: 'optional',
            estimatedTime: '1 min',
            icon: '🎨',
            impact: 'Build brand recognition'
        },
        {
            id: 'social',
            title: 'Social Media Links',
            description: 'Connect your business social accounts',
            completed: socialLinks.length > 0,
            action: () => navigate('/personalization'),
            priority: 'optional',
            estimatedTime: '1 min',
            icon: '🌐',
            impact: 'Increase customer engagement'
        }
    ];

    // Calculate completion stats
    const completedTasks = setupTasks.filter(task => task.completed).length;
    const pendingTasks = setupTasks.filter(task => !task.completed);
    const criticalTasks = pendingTasks.filter(task => task.priority === 'critical');
    const progress = (completedTasks / setupTasks.length) * 100;

    // Don't render if all tasks are completed or component is dismissed
    if (pendingTasks.length === 0 || isDismissed) {
        return null;
    }

    // Get next priority task
    const nextTask = pendingTasks.find(task => task.priority === 'critical') || pendingTasks[0];

    // Priority configurations
    const getPriorityConfig = (priority) => {
        switch (priority) {
            case 'critical':
                return {
                    color: 'text-red-700',
                    bg: 'bg-red-50',
                    border: 'border-red-200',
                    buttonBg: 'bg-red-600 hover:bg-red-700',
                    icon: AlertTriangle,
                    badge: 'bg-red-100 text-red-800'
                };
            case 'important':
                return {
                    color: 'text-orange-700',
                    bg: 'bg-orange-50',
                    border: 'border-orange-200',
                    buttonBg: 'bg-orange-600 hover:bg-orange-700',
                    icon: Clock,
                    badge: 'bg-orange-100 text-orange-800'
                };
            default:
                return {
                    color: 'text-blue-700',
                    bg: 'bg-blue-50',
                    border: 'border-blue-200',
                    buttonBg: 'bg-[#191990] hover:bg-[#191940]',
                    icon: Zap,
                    badge: 'bg-blue-100 text-blue-800'
                };
        }
    };

    const nextTaskConfig = getPriorityConfig(nextTask?.priority);

    // Compact default view
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 overflow-hidden">
            {/* Compact Header */}
            <div className="p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-[#191970] to-blue-600 rounded-full flex items-center justify-center relative">
                            <span className="text-white text-sm font-bold">{pendingTasks.length}</span>
                            {criticalTasks.length > 0 && (
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-900 text-lg">
                                    Store Setup
                                </h3>
                                <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                    {Math.round(progress)}%
                                </span>
                            </div>
                            <p className="text-sm text-gray-600">
                                {pendingTasks.length} task{pendingTasks.length > 1 ? 's' : ''} remaining
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-1.5 text-gray-400 bg-gray-50 hover:text-gray-600 transition-colors rounded"
                            aria-label={isExpanded ? "Collapse" : "Expand"}
                        >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <button
                            onClick={() => setIsDismissed(true)}
                            className="p-1.5 text-gray-400 bg-gray-50 hover:text-gray-600 transition-colors rounded"
                            aria-label="Dismiss"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-3">
                    <div
                        className="bg-gradient-to-r from-[#191970] to-blue-600 h-1.5 rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                {/* Next task preview - only when collapsed */}
                {!isExpanded && nextTask && (
                    <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-lg">{nextTask.icon}</span>
                            <div>
                                <p className="text-lg font-medium text-gray-900">{nextTask.title}</p>
                                <p className="text-sm text-gray-500">{nextTask.estimatedTime}</p>
                            </div>
                        </div>
                        <button
                            onClick={nextTask.action}
                            className={`px-3 py-1.5 rounded text-sm font-medium text-white transition-colors ${nextTaskConfig.buttonBg} flex items-center gap-1`}
                        >
                            Start
                            <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                )}
            </div>

            {/* Expanded content */}
            {isExpanded && (
                <div className="border-t border-gray-100">
                    {/* Next Task Highlight */}
                    {nextTask && (
                        <div className={`p-4 ${nextTaskConfig.bg} border-b border-gray-100`}>
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0">
                                    <div className={`w-10 h-10 ${nextTaskConfig.bg} ${nextTaskConfig.border} border rounded-lg flex items-center justify-center`}>
                                        <span className="text-lg">{nextTask.icon}</span>
                                    </div>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h4 className={`font-semibold text-lg ${nextTaskConfig.color}`}>
                                            Next: {nextTask.title}
                                        </h4>
                                        <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${nextTaskConfig.badge}`}>
                                            {nextTask.priority}
                                        </span>
                                    </div>

                                    <p className="text-sm text-gray-600 mb-2">
                                        {nextTask.description}
                                    </p>

                                    <button
                                        onClick={nextTask.action}
                                        className={`px-4 py-2 rounded text-sm font-medium text-white transition-colors ${nextTaskConfig.buttonBg}`}
                                    >
                                        {nextTask.id === 'payment' ? 'Set Up Payments' : `Complete ${nextTask.title}`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* All Tasks List */}
                    <div className="p-4">
                        <h4 className="font-medium text-gray-900 text-lg mb-3">All Tasks</h4>

                        <div className="space-y-2">
                            {setupTasks.map((task) => {
                                const config = getPriorityConfig(task.priority);

                                return (
                                    <div
                                        key={task.id}
                                        className={`p-2 rounded border transition-all ${task.completed
                                            ? 'bg-green-50 border-green-200'
                                            : `${config.bg} ${config.border}`
                                            }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="flex-shrink-0">
                                                {task.completed ? (
                                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                                ) : (
                                                    <div className={`w-4 h-4 border rounded-full ${config.border}`} />
                                                )}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm">{task.icon}</span>
                                                    <span className={`font-medium text-sm ${task.completed ? 'text-green-800' : 'text-gray-900'
                                                        }`}>
                                                        {task.title}
                                                    </span>
                                                    {!task.completed && (
                                                        <span className="text-sm text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                                            {task.estimatedTime}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {!task.completed && (
                                                <button
                                                    onClick={task.action}
                                                    className={`px-2 py-1 rounded text-sm font-medium text-white transition-colors ${config.buttonBg}`}
                                                >
                                                    Start
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StoreSetupProgress;